import { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { api } from "../lib/api";

/**
 * Compact, dark-aware social-proof strip displayed directly under the Hero.
 * Pulls the 2 most recently-approved reviews and rotates them every 6 seconds.
 * Falls back gracefully (renders nothing) if the API call fails.
 */
export const HeroInlineQuotes = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api
      .get("/testimonials")
      .then((r) => setItems((r.data?.testimonials || []).slice(0, 4)))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setActive((i) => (i + 1) % items.length), 6500);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;
  const t = items[active];

  return (
    <section
      data-testid="hero-inline-quotes"
      className="bg-[#0A192F] border-t border-white/5"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-10 flex items-start sm:items-center gap-5">
        <Quote className="w-9 h-9 text-[#C5A059]/70 flex-shrink-0 hidden sm:block" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex gap-0.5">
              {[...Array(t.rating || 5)].map((_, i) => (
                <Star key={i} className="w-3 h-3 fill-[#C5A059] text-[#C5A059]" />
              ))}
            </div>
            <span className="font-accent text-[10px] tracking-[2px] text-white/40 uppercase">
              {t.source === "customer" ? "Verified buyer" : "Founder story"}
            </span>
          </div>
          <p
            key={t.id}
            className="text-white/85 text-sm sm:text-base leading-relaxed fade-up line-clamp-2"
            data-testid="hero-quote-text"
          >
            &ldquo;{t.quote}&rdquo;
          </p>
          <div className="text-[#C5A059] text-xs mt-2 font-accent tracking-wide">
            {t.name}
            {t.role ? <span className="text-white/40"> &middot; {t.role}</span> : null}
          </div>
        </div>
        {items.length > 1 && (
          <div className="hidden sm:flex flex-col gap-1.5">
            {items.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition ${
                  i === active ? "bg-[#C5A059]" : "bg-white/20"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
